/**
 * Ghost Authentication and Cross-Suite Single Sign-On (SSO) Configuration.
 * Compatible with aixenergy.io, map.aixenergy.io, monitor.aixenergy.io, and capital.aixenergy.io.
 */

export const GHOST_URL =
  (import.meta as any).env?.VITE_GHOST_URL?.replace(/\/$/, "") ?? "https://www.aixenergy.io";

export const GHOST_SIGNIN_URL =
  (import.meta as any).env?.VITE_GHOST_SIGNIN_URL ?? "https://www.aixenergy.io/#/portal/signin";

export const GHOST_SIGNUP_URL =
  (import.meta as any).env?.VITE_GHOST_SIGNUP_URL ?? "https://www.aixenergy.io/#/portal/signup";

export const STUDIO_SIGNED_OUT_KEY = "aixenergy_studio_signed_out";
export const STUDIO_LAST_MEMBER_KEY = "aixenergy_studio_last_member";
export const STUDIO_AUTH_REFRESH_EVENT = "aixenergy-studio-auth-refresh";

export const GHOST_MAP_AUTH_PARAM = "aixenergy_map_auth";
export const GHOST_MAP_RETURN_PARAM = "aixenergy_map_return";

/**
 * Builds the Ghost sign-in URL with proper redirect encoding.
 */
export function ghostSigninUrl(returnUrl?: string): string {
  if (!returnUrl) return GHOST_SIGNIN_URL;
  const separator = GHOST_SIGNIN_URL.includes("?") ? "&" : "?";
  return `${GHOST_SIGNIN_URL}${separator}redirect=${encodeURIComponent(returnUrl)}`;
}

/**
 * Builds the handoff URL that triggers the Ghost session probe script on www.aixenergy.io.
 */
export function ghostStudioAuthHandoffUrl(
  studioReturnUrl: string,
  options?: { embed?: boolean; parentOrigin?: string },
): string {
  const handoff = new URL("/", `${GHOST_URL}/`);
  handoff.searchParams.set(GHOST_MAP_AUTH_PARAM, "1");
  handoff.searchParams.set(GHOST_MAP_RETURN_PARAM, studioReturnUrl);

  const parentOrigin =
    options?.parentOrigin ||
    (typeof window !== "undefined" ? window.location.origin : "https://studio.aixenergy.io");
  handoff.searchParams.set("parent_origin", parentOrigin);

  if (options?.embed) {
    handoff.searchParams.set("embed", "1");
  } else {
    handoff.hash = "/portal/signin";
  }

  return handoff.toString();
}

/**
 * Builds the embed probe URL for the hidden iframe.
 */
export function ghostStudioAuthEmbedUrl(studioReturnUrl?: string): string {
  const returnUrl =
    studioReturnUrl ??
    (typeof window !== "undefined" ? window.location.href : "https://studio.aixenergy.io/");
  const parentOrigin =
    typeof window !== "undefined" ? window.location.origin : "https://studio.aixenergy.io";

  return ghostStudioAuthHandoffUrl(returnUrl, {
    embed: true,
    parentOrigin,
  });
}

/**
 * Whitelist of trusted origins for postMessage session token handoffs.
 */
export function ghostOrigins(): string[] {
  const origins = new Set<string>([
    "https://aixenergy.io",
    "https://www.aixenergy.io",
    "https://map.aixenergy.io",
    "https://maps.aixenergy.io",
    "https://monitor.aixenergy.io",
    "https://capital.aixenergy.io",
    "https://studio.aixenergy.io",
  ]);
  if (typeof window !== "undefined") {
    origins.add(window.location.origin);
  }
  return Array.from(origins);
}

/**
 * Clears signed-out flag so auto-auth can re-probe.
 */
export function clearStudioSignedOut(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(STUDIO_SIGNED_OUT_KEY);
    window.localStorage.removeItem(STUDIO_SIGNED_OUT_KEY);
  } catch {}
}

/**
 * Handles Ghost portal sign-in popup or tab window with active handshake listening.
 */
export function handleGhostSignInClick(
  event: { preventDefault: () => void },
  signinUrl?: string,
): void {
  event.preventDefault();
  clearStudioSignedOut();

  const currentUrl = typeof window !== "undefined" ? window.location.href : "https://studio.aixenergy.io/";
  const currentOrigin = typeof window !== "undefined" ? window.location.origin : "https://studio.aixenergy.io";
  const targetUrl = signinUrl || ghostStudioAuthHandoffUrl(currentUrl, { parentOrigin: currentOrigin });

  // A dedicated tab name enables window.opener postMessage handoff from Ghost portal
  const signinWindow = window.open(targetUrl, "aixenergy-ghost-auth");
  if (!signinWindow) {
    if (typeof window !== "undefined") {
      window.location.assign(targetUrl);
    }
    return;
  }

  signinWindow.focus();

  const timer = window.setInterval(() => {
    if (signinWindow.closed) {
      window.clearInterval(timer);
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event(STUDIO_AUTH_REFRESH_EVENT));
      }
    }
  }, 1000);
}
